import useGameStore from '../store/gameStore';
import type { BotBroadcastPhase } from '../types/gameTypes';
import { money } from '../lib/format';

/**
 * Bot turn play-by-play bar. Every bot phase is broadcast from the store — the engine
 * never skips silently through a rival's turn. Rendered in the centre of the board.
 */
const PHASE_META: Record<BotBroadcastPhase, { icon: string; label: string }> = {
  ROLL: { icon: '🎲', label: 'Rolling' },
  ROLLED: { icon: '🎯', label: 'Movement' },
  MOVE: { icon: '🚶', label: 'Moving' },
  SETTLE: { icon: '💵', label: 'Settlement' },
  GOVERN: { icon: '🏛️', label: 'Governance' },
};

const BotStatusBar = () => {
  const botStatus = useGameStore((s) => s.botStatus);
  const players = useGameStore((s) => s.players);
  const currentPlayerIndex = useGameStore((s) => s.currentPlayerIndex);
  const attention = useGameStore((s) => s.attention);
  const tradeOffer = useGameStore((s) => s.tradeOffer);
  const turnPhase = useGameStore((s) => s.turnPhase);
  const board = useGameStore((s) => s.board);
  const respondToTrade = useGameStore((s) => s.respondToTrade);

  const active = players[currentPlayerIndex];

  // Incoming offer review: the splash was acknowledged, the deal is still on the table.
  const reviewing = turnPhase === 'TRADE_NEGOTIATION' && Boolean(tradeOffer) && tradeOffer?.status === 'PENDING' && !attention;
  if (reviewing && tradeOffer) {
    const sender = players.find((p) => p.id === tradeOffer.senderId);
    const nameOf = (tileId: string) => board.find((t) => t.id === tileId)?.name ?? tileId;
    return (
      <div className="bot-status-bar rounded-xl border-2 border-violet-400/60 bg-slate-900/90 p-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">Trade negotiation · offer on the table</p>
        <p className="mt-1 text-sm font-bold text-slate-100">{sender?.name ?? 'Rival'} awaits your answer</p>
        <p className="mt-0.5 text-[11px] text-slate-300">
          Offers {money(tradeOffer.offeredCash)} + {tradeOffer.offeredShares.map((l) => `${l.shares} sh ${nameOf(l.tileId)}`).join(', ') || '—'} · requests {money(tradeOffer.requestedCash)} +{' '}
          {tradeOffer.requestedShares.map((l) => `${l.shares} sh ${nameOf(l.tileId)}`).join(', ') || '—'}
        </p>
        <div className="mt-2 flex gap-2">
          <button onClick={() => respondToTrade(true)} className="min-h-[44px] flex-1 rounded-xl border-2 border-emerald-400 bg-emerald-500/90 px-3 text-sm font-black text-slate-950 hover:bg-emerald-400">
            ✅ Accept
          </button>
          <button onClick={() => respondToTrade(false)} className="min-h-[44px] flex-1 rounded-xl border-2 border-rose-500 bg-slate-800 px-3 text-sm font-extrabold text-rose-200 hover:bg-slate-700">
            ❌ Reject
          </button>
        </div>
      </div>
    );
  }

  if (!botStatus || attention || !active?.isAI) return null;
  const meta = PHASE_META[botStatus.phase];
  return (
    <div key={`${botStatus.playerId}-${botStatus.phase}-${botStatus.text}`} className="bot-status-bar rounded-xl border border-cyan-400/40 bg-slate-900/85 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">
          Bot turn · {meta.label}
        </span>
        <span className="ml-auto text-[11px] text-slate-500">speed: top bar ⏱</span>
      </div>
      <p className="mt-1 text-sm font-semibold text-slate-100">
        {meta.icon} {botStatus.text}
      </p>
    </div>
  );
};

export default BotStatusBar;
